import React from 'react';
import clsx from 'clsx';
import { AppBar, CssBaseline, makeStyles, Toolbar, useMediaQuery, useTheme } from '@material-ui/core';

import { drawerWidth } from '../../constant';
import Header from './Header';
import Sidebar from '../Sidebar';

import Breadcrumb from '../../../Control/Breadcrumb';
import navigation from '../../../Route/navItems';

import { IconChevronRight } from '@tabler/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootReduxState } from '../../../Redux/Store';
import { isTestEnvironment } from '../../../Core/envHelper';

interface StyleProps {
    isTestEnv: boolean;
}

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex'
    },
    appBar: {
        backgroundColor: (props: StyleProps) => props.isTestEnv ? '#ffcdd2' : theme.palette.background.default
    },
    appBarWidth: {
        transition: theme.transitions.create('width'),
        backgroundColor: (props: StyleProps) => props.isTestEnv ? '#ffcdd2' : theme.palette.background.default
    },
    content: {
        ...theme.typography.mainContent,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: -(drawerWidth - 20),
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginRight: '10px'
        }
    },
    contentShift: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        marginLeft: 0,
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            marginBottom: '10px',
        }
    }
}));

interface MainLayoutProps {
    children: React.ReactNode
}

const MainLayout = ({ children }: MainLayoutProps) => {
    const isTestEnv = isTestEnvironment();
    const classes = useStyles({ isTestEnv });
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));

    // Handle left drawer
    const leftDrawerOpened = useSelector((state: RootReduxState) => state.customization.opened);
    const dispatch = useDispatch();
    const handleLeftDrawerToggle = () => {
        dispatch({ type: 'SET_MENU', Data: !leftDrawerOpened });
    };

    React.useEffect(() => {
        const openLeftDrawerState = (val: boolean) => {
            dispatch({ type: 'SET_MENU', Data: val });
        };
        openLeftDrawerState(matchUpMd);
    }, [dispatch, matchUpMd]);

    return (
        <div className={classes.root}>
            <CssBaseline />
            <AppBar position="fixed" color="inherit" elevation={0} className={leftDrawerOpened ? classes.appBarWidth : classes.appBar}>
                <Toolbar>
                    <Header isTestEnv={isTestEnv} handleLeftDrawerToggle={handleLeftDrawerToggle} />
                </Toolbar>
            </AppBar>
            <Sidebar drawerOpen={leftDrawerOpened} drawerToggle={handleLeftDrawerToggle} />
            <main
                className={clsx([
                    classes.content,
                    {
                        [classes.contentShift]: leftDrawerOpened
                    }
                ])}
            >
                <Breadcrumb separator={IconChevronRight} navigation={navigation()} icon title rightAlign />
                <div>{children}</div>
            </main>
        </div>
    );
};

export default MainLayout;
